import { useEffect, useState, useRef } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  getCaseStudies, createCaseStudy, updateCaseStudy, deleteCaseStudy,
  uploadCaseStudyPdf, getVerticals
} from "@/lib/api";
import {
  Plus, Pencil, Trash2, Loader2, Upload, FileText, Download,
  Building2, X, BarChart3, Target, CheckCircle2, TrendingUp
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  title: "",
  client_name: "",
  industry: "",
  vertical_id: "",
  challenge: "",
  solution: "",
  results: "",
  metrics: [
    { label: "", value: "" },
    { label: "", value: "" },
    { label: "", value: "" },
    { label: "", value: "" },
    { label: "", value: "" },
    { label: "", value: "" },
  ],
  download_url: "",
};

export default function AdminCaseStudies() {
  const [studies, setStudies] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [filterVertical, setFilterVertical] = useState("");
  const fileRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [csRes, vRes] = await Promise.all([
        getCaseStudies(filterVertical || undefined),
        getVerticals()
      ]);
      setStudies(csRes.data);
      setVerticals(vRes.data || []);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [filterVertical]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (study) => {
    setEditing(study);
    const metrics = study.metrics || [];
    // Ensure exactly 6 KPI slots
    const paddedMetrics = [...metrics];
    while (paddedMetrics.length < 6) paddedMetrics.push({ label: "", value: "" });
    setForm({
      title: study.title || "",
      client_name: study.client_name || "",
      industry: study.industry || "",
      vertical_id: study.vertical_id || "",
      challenge: study.challenge || "",
      solution: study.solution || "",
      results: study.results || "",
      metrics: paddedMetrics.slice(0, 6),
      download_url: study.download_url || "",
    });
    setDialogOpen(true);
  };

  const handleVerticalChange = (verticalId) => {
    const v = verticals.find(v => v.id === verticalId);
    setForm(prev => ({
      ...prev,
      vertical_id: verticalId,
      industry: v ? v.name : prev.industry
    }));
  };

  const handleMetricChange = (index, field, value) => {
    setForm(prev => {
      const newMetrics = [...prev.metrics];
      newMetrics[index] = { ...newMetrics[index], [field]: value };
      return { ...prev, metrics: newMetrics };
    });
  };

  const handleSave = async () => {
    if (!form.title || !form.client_name || !form.vertical_id) {
      toast.error("Title, Client Name, and Vertical are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        metrics: form.metrics.filter(m => m.label && m.value)
      };
      if (editing) {
        await updateCaseStudy(editing.id, payload);
        toast.success("Case study updated");
      } else {
        await createCaseStudy(payload);
        toast.success("Case study created");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error("Failed to save case study");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this case study?")) return;
    try {
      await deleteCaseStudy(id);
      toast.success("Case study deleted");
      fetchAll();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handlePdfUpload = async (studyId, file) => {
    setUploading(studyId);
    try {
      const res = await uploadCaseStudyPdf(studyId, file);
      toast.success("PDF uploaded successfully");
      fetchAll();
    } catch (e) {
      toast.error("Failed to upload PDF");
    } finally {
      setUploading(null);
    }
  };

  const getVerticalName = (verticalId) => {
    const v = verticals.find(v => v.id === verticalId);
    return v ? v.name : "—";
  };

  return (
    <AdminLayout title="Case Studies">
      <div className="space-y-6" data-testid="admin-case-studies">
        {/* Header with filter and add button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-slate-600 text-sm">Manage case studies across verticals. Upload PDFs for download.</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={filterVertical}
              onChange={e => setFilterVertical(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="filter-vertical-select"
            >
              <option value="">All Verticals</option>
              {verticals.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
            <Button onClick={openCreate} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-case-study-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Case Study
            </Button>
          </div>
        </div>

        {/* Case Studies List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : studies.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No case studies yet</p>
            <p className="text-slate-500 text-sm mt-1">Click "Add Case Study" to create one</p>
          </div>
        ) : (
          <div className="space-y-4">
            {studies.map(study => (
              <div
                key={study.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm"
                data-testid={`admin-cs-card-${study.id}`}
              >
                <div className="flex items-start justify-between p-5 gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-[#0066CC] text-xs font-medium">
                        <Building2 className="w-3 h-3" />
                        {getVerticalName(study.vertical_id)}
                      </span>
                    </div>
                    <h3 className="font-semibold text-slate-900 text-lg">{study.title}</h3>
                    <p className="text-slate-500 text-sm mt-0.5">Client: {study.client_name}</p>

                    {/* KPI chips */}
                    {study.metrics && study.metrics.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {study.metrics.map((m, i) => (
                          <span key={`${m.label}-${m.value}-${i}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-50 border border-slate-100 text-xs">
                            <BarChart3 className="w-3 h-3 text-[#0066CC]" />
                            <span className="font-semibold text-slate-900">{m.value}</span>
                            <span className="text-slate-500">{m.label}</span>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* PDF status */}
                    <div className="flex items-center gap-3 mt-3">
                      {study.download_url ? (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}${study.download_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-emerald-600 font-medium hover:underline"
                          data-testid={`cs-download-${study.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          PDF Attached
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs text-amber-600">
                          <FileText className="w-3.5 h-3.5" />
                          No PDF uploaded
                        </span>
                      )}
                      <label className="inline-flex items-center gap-1.5 text-xs text-[#0066CC] font-medium cursor-pointer hover:underline">
                        <Upload className="w-3.5 h-3.5" />
                        {uploading === study.id ? "Uploading..." : "Upload PDF"}
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={e => {
                            if (e.target.files[0]) handlePdfUpload(study.id, e.target.files[0]);
                            e.target.value = "";
                          }}
                          disabled={uploading === study.id}
                          data-testid={`cs-upload-${study.id}`}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(study)} data-testid={`cs-edit-${study.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(study.id)} data-testid={`cs-delete-${study.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="cs-dialog-title">
                {editing ? "Edit Case Study" : "New Case Study"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-2">
              {/* Vertical Selection */}
              <div className="space-y-2">
                <Label>Vertical / Industry *</Label>
                <select
                  value={form.vertical_id}
                  onChange={e => handleVerticalChange(e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="cs-vertical-select"
                >
                  <option value="">Select a vertical</option>
                  {verticals.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>

              {/* Title & Client */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title of Case Study *</Label>
                  <Input
                    value={form.title}
                    onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g., Digital Transformation of XYZ Ltd"
                    data-testid="cs-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input
                    value={form.client_name}
                    onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))}
                    placeholder="e.g., Supreme Packaging Ltd"
                    data-testid="cs-client-input"
                  />
                </div>
              </div>

              {/* Challenge */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-red-500" />
                  The Challenge
                </Label>
                <Textarea
                  value={form.challenge}
                  onChange={e => setForm(p => ({ ...p, challenge: e.target.value }))}
                  placeholder="Describe the business challenges the client faced..."
                  rows={4}
                  className="resize-y"
                  data-testid="cs-challenge-input"
                />
              </div>

              {/* Solution */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Our Solution
                </Label>
                <Textarea
                  value={form.solution}
                  onChange={e => setForm(p => ({ ...p, solution: e.target.value }))}
                  placeholder="Describe the ERP solution implemented..."
                  rows={4}
                  className="resize-y"
                  data-testid="cs-solution-input"
                />
              </div>

              {/* Results */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#0066CC]" />
                  Results & Impact
                </Label>
                <Textarea
                  value={form.results}
                  onChange={e => setForm(p => ({ ...p, results: e.target.value }))}
                  placeholder="Describe the measurable outcomes and business impact..."
                  rows={4}
                  className="resize-y"
                  data-testid="cs-results-input"
                />
              </div>

              {/* 6 KPIs */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#0066CC]" />
                  Key Performance Indicators (6 KPIs)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {form.metrics.map((metric, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <span className="text-xs font-bold text-slate-400 w-5">{index + 1}</span>
                      <Input
                        value={metric.label}
                        onChange={e => handleMetricChange(index, "label", e.target.value)}
                        placeholder="KPI Title"
                        className="flex-1 h-8 text-sm"
                        data-testid={`cs-kpi-label-${index}`}
                      />
                      <Input
                        value={metric.value}
                        onChange={e => handleMetricChange(index, "value", e.target.value)}
                        placeholder="Result"
                        className="w-28 h-8 text-sm"
                        data-testid={`cs-kpi-value-${index}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="cs-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {editing ? "Update Case Study" : "Create Case Study"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
